// app/javascript/components/ColumnsConstructor/ColumnForm.jsx
import React from 'react';
import Select from 'react-select';
import { Save, X, Layers } from 'lucide-react';
import { 
  SELECT_STYLES,
  COLUMN_TYPE_DESCRIPTIONS,
  getFieldOptions,
  findSelectedValue,
  findSelectedValues,
  getErrorCategoryOptions,
  getAllFieldOptions
} from './columnsUtils';

// Компонент конфигурации для разных типов колонок
const ColumnTypeConfig = ({ 
  formData, 
  onFormChange, 
  onConfigChange, 
  onMultiSelectChange, 
  availableFields, 
  columns, 
  errorCategories 
}) => {
  const { column_type } = formData;
  const fieldOptions = getFieldOptions(availableFields, columns);

  switch (column_type) {
    case 'calculated':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Formula</label>
            <Select
              value={formData.calculation_config.formula ? 
                  { value: formData.calculation_config.formula, label: formData.calculation_config.formula } : 
                  null
              }
              onChange={(option) => onConfigChange('formula', option?.value)}
              options={[
                  { value: 'sum', label: 'Sum' },
                  { value: 'subtract', label: 'Subtract' },
                  { value: 'multiply', label: 'Multiply' }
              ]}
              styles={SELECT_STYLES}
              placeholder="Select formula"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source Columns</label>
            <Select
              isMulti
              value={findSelectedValues(formData.calculation_config.source_columns || [], availableFields, columns)}
              onChange={(selectedOptions) => onMultiSelectChange('source_columns', selectedOptions)}
              options={fieldOptions}
              styles={SELECT_STYLES}
              placeholder="Select source columns"
            />
          </div>
        </div>
      );

    case 'percentage_ratio':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Numerator</label>
            <Select
              value={findSelectedValue(formData.calculation_config.numerator, availableFields, columns)}
              onChange={(option) => onConfigChange('numerator', option?.value || '')}
              options={fieldOptions}
              styles={SELECT_STYLES}
              placeholder="Select numerator column"
              isClearable
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Denominator</label>
            <Select
              value={findSelectedValue(formData.calculation_config.denominator, availableFields, columns)}
              onChange={(option) => onConfigChange('denominator', option?.value || '')}
              options={fieldOptions}
              styles={SELECT_STYLES}
              placeholder="Select denominator column"
              isClearable
            />
          </div>
        </div>
      );

    case 'percentage_attempts':
      return (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Field</label>
            <Select
              value={findSelectedValue(formData.calculation_config.target, availableFields, columns)}
              onChange={(option) => onConfigChange('target', option?.value || '')}
              options={fieldOptions}
              styles={SELECT_STYLES}
              placeholder="Select target column"
              isClearable
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Formula</label>
            <Select
              value={{ value: formData.calculation_config.total_formula || 'sum', label: 'Sum' }}
              onChange={(option) => onConfigChange('total_formula', option?.value || 'sum')}
              options={[
                { value: 'sum', label: 'Sum' }
              ]}
              styles={SELECT_STYLES}
              placeholder="Select total formula"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Columns</label>
            <Select
              isMulti
              value={findSelectedValues(formData.calculation_config.total_columns || [], availableFields, columns)}
              onChange={(selectedOptions) => onMultiSelectChange('total_columns', selectedOptions)}
              options={fieldOptions}
              styles={SELECT_STYLES}
              placeholder="Select columns for total calculation"
            />
          </div>
        </div>
      );

    case 'error_category':
      const errorCategoryOptions = getErrorCategoryOptions(errorCategories);
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Error Category</label>
          <Select
            value={errorCategoryOptions.find(opt => opt.value === formData.data_source)}
            onChange={(option) => onFormChange('data_source', option?.value || '')}
            options={errorCategoryOptions}
            styles={SELECT_STYLES}
            placeholder="Select error category"
            isClearable
          />
        </div>
      );

    default:
      const dataSourceOptions = (availableFields || []).map(([label, value]) => ({ 
        value, 
        label: `📊 ${label}`
      }));
      return (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data Source</label>
          <Select
            value={dataSourceOptions.find(opt => opt.value === formData.data_source)}
            onChange={(option) => onFormChange('data_source', option?.value || '')}
            options={dataSourceOptions}
            styles={SELECT_STYLES}
            placeholder="Select data source"
            isClearable
          />
        </div>
      );
  }
};

// Форма создания/редактирования колонки
export const ColumnForm = ({ 
  isOpen, 
  onClose, 
  formData, 
  onFormChange, 
  onConfigChange, 
  onMultiSelectChange, 
  onSave, 
  editingColumn, 
  availableFields, 
  columns, 
  errorCategories, 
  columnTypes, 
  isLoading, 
  isSaving, 
  userStatus 
}) => {
  if (!isOpen) return null;

  const handleFormChange = (field, value) => {
    onFormChange(field, value);
  };

  const handleConfigChange = (field, value) => {
    onConfigChange(field, value);
  };

  const handleMultiSelectChange = (field, selectedOptions) => {
    onMultiSelectChange(field, selectedOptions);
  };

  // Фильтруем типы колонок - убираем chart_group из обычных колонок
  const filteredColumnTypes = columnTypes ? columnTypes.filter(type => type !== 'chart_group') : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-90vh overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">
            {editingColumn ? 'Edit Column' : 'Add New Column'}
            {!userStatus.is_admin && (
              <span className="text-sm font-normal text-blue-600 ml-2">
                ({userStatus.is_personalized ? 'Personal' : 'Will create personal copy'})
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Basic fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Column Key</label>
              <input
                type="text"
                value={formData.key || ''}
                onChange={(e) => handleFormChange('key', e.target.value)}
                placeholder="e.g., custom_metric"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Label</label>
              <input
                type="text"
                value={formData.label || ''}
                onChange={(e) => handleFormChange('label', e.target.value)}
                placeholder="e.g., Custom Metric"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description (Tooltip)</label>
            <textarea
              value={formData.description || ''}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder="Description that will appear in tooltip"
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Column Type</label>
              <Select
                value={{ value: formData.column_type, label: formData.column_type?.replace('_', ' ') || 'base' }}
                onChange={(option) => handleFormChange('column_type', option?.value || 'base')}
                options={filteredColumnTypes.map(type => ({
                  value: type,
                  label: type.replace('_', ' ')
                }))}
                styles={SELECT_STYLES}
                placeholder="Select column type"
              />
              <p className="text-xs text-gray-500 mt-1">
                {COLUMN_TYPE_DESCRIPTIONS[formData.column_type]}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Chart Color</label>
              <input
                type="color"
                value={formData.chart_color || '#10B981'}
                onChange={(e) => handleFormChange('chart_color', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
              />
            </div>
          </div>

          {/* Configuration based on type */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Configuration</h3>
            <ColumnTypeConfig
              formData={formData}
              onFormChange={handleFormChange}
              onConfigChange={handleConfigChange}
              onMultiSelectChange={handleMultiSelectChange}
              availableFields={availableFields}
              columns={columns}
              errorCategories={errorCategories}
            />
          </div>

          {/* Checkboxes */}
          <div className="flex gap-6 pt-4 border-t">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_visible !== false}
                onChange={(e) => handleFormChange('is_visible', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Visible by default</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.show_on_chart || false}
                onChange={(e) => handleFormChange('show_on_chart', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Show on chart</span>
            </label>
          </div>

          {/* Form actions */}
          <div className="flex justify-end gap-3 pt-6 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={isLoading || isSaving}
              className={`px-4 py-2 rounded-md transition-colors flex items-center gap-2 ${
                isLoading || isSaving
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              <Save className="h-4 w-4" />
              {isLoading || isSaving ? 'Saving...' : 'Save Column'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Форма создания/редактирования Chart Group
export const ChartGroupForm = ({ 
  isOpen, 
  onClose, 
  formData, 
  onFormChange, 
  onMetricsChange, 
  onMetricColorChange, 
  onSave, 
  editingChartGroup, 
  availableFields, 
  columns, 
  isLoading, 
  isSaving, 
  userStatus 
}) => {
  if (!isOpen) return null;

  const handleFormChange = (field, value) => {
    onFormChange(field, value);
  };

  const handleMetricsChange = (selectedOptions) => {
    onMetricsChange(selectedOptions);
  };

  const handleMetricColorChange = (metric, color) => {
    onMetricColorChange(metric, color);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-90vh overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Layers className="h-5 w-5" />
            {editingChartGroup ? 'Edit Chart Group' : 'Add New Chart Group'}
            {!userStatus.is_admin && (
              <span className="text-sm font-normal text-blue-600 ml-2">
                ({userStatus.is_personalized ? 'Personal' : 'Will create personal copy'})
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Basic fields for chart group */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Group Key</label>
              <input
                type="text"
                value={formData.key || ''}
                onChange={(e) => handleFormChange('key', e.target.value)}
                placeholder="e.g., registration_flow"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Label</label>
              <input
                type="text"
                value={formData.label || ''}
                onChange={(e) => handleFormChange('label', e.target.value)}
                placeholder="e.g., Registration Flow"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Group Name</label>
            <input
              type="text"
              value={formData.group_name || ''}
              onChange={(e) => handleFormChange('group_name', e.target.value)}
              placeholder="e.g., Registration Process"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-gray-500 mt-1">This name will be used in chart legends</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Group Border Color</label>
            <input
              type="color"
              value={formData.chart_color || '#10B981'}
              onChange={(e) => handleFormChange('chart_color', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 h-10"
            />
            <p className="text-xs text-gray-500 mt-1">Border color for the stacked group (individual metrics have their own colors)</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Metrics to Group (minimum 2)
            </label>
            <Select
              isMulti
              value={findSelectedValues(formData.metrics || [], availableFields, columns, true)}
              onChange={handleMetricsChange}
              options={getFieldOptions(availableFields, columns, true)}
              styles={SELECT_STYLES}
              placeholder="Select metrics for grouping"
            />
            <p className="text-xs text-gray-500 mt-1">Select at least 2 metrics that will be shown as stacked bars</p>
          </div>
          
          {formData.metrics && formData.metrics.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Metric Colors</label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {formData.metrics.map(metric => {
                  const option = getAllFieldOptions(availableFields, columns, true).find(opt => opt.value === metric);
                  const metricLabel = option ? option.label : metric;
                  const color = formData.colors?.[metric] || '#10B981';
                  
                  return (
                    <div key={metric} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                      <span className="text-sm text-gray-700 font-medium">{metricLabel}</span>
                      <input
                        type="color"
                        value={color}
                        onChange={(e) => handleMetricColorChange(metric, e.target.value)}
                        className="w-10 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Form actions */}
          <div className="flex justify-end gap-3 pt-6 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={isLoading || isSaving || !formData.metrics || formData.metrics.length < 2}
              className={`px-4 py-2 rounded-md transition-colors flex items-center gap-2 ${
                isLoading || isSaving || !formData.metrics || formData.metrics.length < 2
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-indigo-500 hover:bg-indigo-600 text-white'
              }`}
            >
              <Save className="h-4 w-4" />
              {isLoading || isSaving ? 'Saving...' : 'Save Chart Group'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};